/**
 * The confirm step for moving an endpoint to another data buyer.
 *
 * Says what the move does before it happens: only `destinations.buyer_id`
 * changes, so new rows sell under the new buyer while everything already sold
 * stays with the old one. The one quiet effect is price: a link with no price
 * of its own, on an endpoint with none, prices from the buyer's legacy
 * settings, so it moves with the buyer. The server works that out with the
 * same chain the engine runs; see docs/DOMAIN.md, Moving a destination to
 * another buyer.
 */
import { Alert, Badge, Button, Group, Loader, Modal, Stack, Table, Text } from '@mantine/core';
import { useDestinationBuyerChange } from '../api/queries';
import { formatGBP } from '../theme';
import { useVocab } from '../vocab';

export function DestinationBuyerChangeModal({
  destinationId,
  destinationName,
  buyerId,
  buyerName,
  opened,
  saving,
  onCancel,
  onConfirm,
}: {
  destinationId: string | undefined;
  destinationName: string;
  /** The buyer it would move to. */
  buyerId: string | null;
  buyerName: string;
  opened: boolean;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const v = useVocab();
  const preview = useDestinationBuyerChange(destinationId, buyerId, opened);
  const change = preview.data;
  const links = change?.links ?? [];
  const oldName = change?.current_buyer_name || '';
  const hadBuyer = Boolean(change?.current_buyer_id);
  const repriced = links.filter((l) => l.price_changes);
  const doubled = links.filter((l) => l.buyer_already_in_group);

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      size="lg"
      title={`Move ${destinationName} to ${buyerName}?`}
    >
      <Stack>
        {preview.error && (
          <Alert color="red" variant="light" title="Could not work out what this changes">
            <Text size="sm">{preview.error.message}</Text>
          </Alert>
        )}
        {preview.isPending && <Group justify="center" p="md"><Loader size="sm" /></Group>}

        {change && (
          <>
            <Alert color="chartAmber" variant="light" title="What happens">
              <Stack gap={6}>
                <Text size="sm">
                  {`New ${v.many('lead')} sent here go to ${buyerName} and are sold under them.`}
                </Text>
                <Text size="sm">
                  {hadBuyer
                    ? `${v.Many('lead')} already sold, returns and statements stay with ${oldName}.`
                    : `${v.Many('lead')} already sent here, returns and statements do not change.`}
                </Text>
                <Text size="sm">
                  {`The ${v.p.urlLower}, caps, filters and schedule stay as they are, so check the ${v.p.urlLower} is the right one for ${buyerName}.`}
                </Text>
                {hadBuyer && (
                  <Text size="sm">
                    {`Duplicate and already sold checks go by ${v.one('buyer')}, so ${v.a('lead')} sold to ${oldName} can reach this ${v.p.urlLower} again if it is redistributed.`}
                  </Text>
                )}
              </Stack>
            </Alert>

            {repriced.length > 0 && (
              <Alert color="red" variant="light" title="The price changes">
                <Text size="sm">
                  {`${repriced.length === 1 ? 'A link here prices' : `${repriced.length} links here price`} from the ${v.one('buyer')}'s settings, with no price of ${repriced.length === 1 ? 'its' : 'their'} own, so the price moves with the ${v.one('buyer')}. To keep today's price, set it on the link under ${v.Many('campaign')} → Delivery first.`}
                </Text>
              </Alert>
            )}

            {doubled.length > 0 && (
              <Text size="sm">
                {`${buyerName} already has another ${v.one('destination')} in ${doubled
                  .map((l) => `${l.campaign_name}, ${l.group_name}`)
                  .join('; ')}. ${v.A('lead')} is sold to ${v.a('buyer')} once, so only one of the two can sell it.`}
              </Text>
            )}

            {links.length === 0 ? (
              <Text size="sm" c="dimmed">
                {`Not linked to any ${v.one('routeGroup')}, so nothing is sent here yet.`}
              </Text>
            ) : (
              <Table.ScrollContainer minWidth={420}>
                <Table verticalSpacing={4}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{v.One('campaign')}</Table.Th>
                      <Table.Th>{v.One('routeGroup')}</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th ta="right">Price</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {links.map((l) => (
                      <Table.Tr key={l.group_id}>
                        <Table.Td><Text size="sm">{l.campaign_name}</Text></Table.Td>
                        <Table.Td><Text size="sm">{l.group_name}</Text></Table.Td>
                        <Table.Td>
                          <Badge size="sm" variant="light" color={l.live ? 'chartGreen' : 'gray'}>
                            {l.live ? 'Live' : 'Paused'}
                          </Badge>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Text size="sm" fw={l.price_changes ? 600 : undefined}>
                            {l.price_changes
                              ? `${formatGBP(l.price_now.resolved)} → ${formatGBP(l.price_after.resolved)}`
                              : formatGBP(l.price_after.resolved)}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onCancel}>Cancel</Button>
          <Button disabled={!change} loading={saving} onClick={onConfirm}>
            {`Move to ${buyerName} and save`}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
